"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import type { TrialState } from "@lib/data/trial"
import TrialClaimModal from "./modal"

type TrialClaimContextValue = {
  /** Opens the shared trial modal (the three entry points all call this). */
  open: () => void
  state: TrialState | null
  loading: boolean
  /** Re-reads /api/trial-eligibility (called after a claim / bind). */
  refresh: () => void
}

const TrialClaimContext = createContext<TrialClaimContextValue | null>(null)

/**
 * Global trial-claim state (0.8.5 工单 08). Mounted once in MainShell so the
 * three entry points — dashboard banner, floating marketing banner, pricing
 * card — share ONE modal and ONE eligibility read. The state is fetched once
 * per full page load (cheap for anonymous: the route short-circuits before
 * touching the engine) and refreshed explicitly after a claim or bind.
 */

/** 一次性标记：弹窗发起 Discord 验证后，回跳的首次刷新要绕开 5 分钟缓存。 */
export const MEMBERSHIP_REFRESH_KEY = "trial:membership-refresh"
export function TrialClaimProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [state, setState] = useState<TrialState | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    // 2026-10-04（item 7）：用户在弹窗里发起了 Discord 绑定/重授权，回跳后
    // 需要一次权威的 membership 复核（插件侧有 5 分钟缓存）。标记只消费一次。
    let refreshMembership = false
    try {
      refreshMembership =
        window.sessionStorage.getItem(MEMBERSHIP_REFRESH_KEY) === "1"
      if (refreshMembership) {
        window.sessionStorage.removeItem(MEMBERSHIP_REFRESH_KEY)
      }
    } catch {
      refreshMembership = false
    }

    fetch(`/api/trial-eligibility${refreshMembership ? "?refresh=1" : ""}`, {
      cache: "no-store",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setState(data as TrialState)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])
  const open = useCallback(() => setIsOpen(true), [])

  const value = useMemo<TrialClaimContextValue>(
    () => ({ open, state, loading, refresh }),
    [open, state, loading, refresh]
  )

  return (
    <TrialClaimContext.Provider value={value}>
      {children}
      {isOpen ? (
        <TrialClaimModal
          state={state}
          loading={loading}
          refresh={refresh}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </TrialClaimContext.Provider>
  )
}

/**
 * Entry-point hook. Outside the provider (isolated renders / tests) it returns
 * a no-op so a CTA never throws — the real provider wraps every (main) page.
 */
export function useTrialClaim(): TrialClaimContextValue {
  const ctx = useContext(TrialClaimContext)
  return (
    ctx ?? { open: () => undefined, state: null, loading: false, refresh: () => undefined }
  )
}
